'use client';

import { useEffect, useRef, useState, useCallback } from 'react';

const StarfieldBackgroundCanvas = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isIdle, setIsIdle] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (canvasRef.current) {
      observer.observe(canvasRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const startAnimation = () => setIsIdle(true);

    // Much longer delay on mobile to reduce initial CPU load
    // Extra long for above-the-fold components
    const idleTimeout = isMobile ? 6000 : 1000; // Increased from 4s to 6s on mobile
    const fallbackTimeout = isMobile ? 5000 : 500; // Increased from 3s to 5s on mobile

    if ('requestIdleCallback' in window) {
      const w = window as Window & {
        requestIdleCallback: (
          cb: IdleRequestCallback,
          options?: IdleRequestOptions,
        ) => number;
        cancelIdleCallback?: (id: number) => void;
      };
      const id = w.requestIdleCallback(startAnimation, {
        timeout: idleTimeout,
      });
      return () => w.cancelIdleCallback?.(id);
    } else {
      const timeout = setTimeout(startAnimation, fallbackTimeout);
      return () => clearTimeout(timeout);
    }
  }, [isVisible, isMobile]);

  const initializeStarfield = useCallback(() => {
    if (typeof window === 'undefined' || !isIdle) return;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    if (prefersReducedMotion) return;

    const STAR_COLOR = 'rgba(205, 205, 205, 0.3)';
    const STAR_SIZE = 3;
    const STAR_MIN_SCALE = 0.2;
    const OVERFLOW_THRESHOLD = 50;
    // Use half stars on mobile
    const isMobile = window.innerWidth < 768;
    const MAX_STAR_COUNT = isMobile ? 40 : 160;
    const STAR_COUNT = Math.min(
      MAX_STAR_COUNT,
      Math.ceil((window.innerWidth + window.innerHeight) / 12),
    );

    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d', { alpha: true });
    if (!context) return;

    let scale = 1;
    let width: number;
    let height: number;
    let gradient: CanvasGradient | null = null;
    const stars: Array<{ x: number; y: number; z: number }> = [];
    let pointerX: number | null = null;
    let pointerY: number | null = null;

    const velocity = { x: 0, y: 0, tx: 0, ty: 0, z: 0.0005 };
    let touchInput = false;
    let animationFrameId: number;
    let lastFrameTime = 0;
    const TARGET_FPS = 30;
    const FRAME_INTERVAL = 1000 / TARGET_FPS;
    let isVisible = true;

    const generate = () => {
      for (let i = 0; i < STAR_COUNT; i++) {
        stars.push({
          x: 0,
          y: 0,
          z: STAR_MIN_SCALE + Math.random() * (1 - STAR_MIN_SCALE),
        });
      }
    };

    const placeStar = (star: { x: number; y: number; z: number }) => {
      star.x = Math.random() * width;
      star.y = Math.random() * height;
    };

    const recycleStar = (star: { x: number; y: number; z: number }) => {
      let direction = 'z';
      const vx = Math.abs(velocity.x);
      const vy = Math.abs(velocity.y);

      if (vx > 1 || vy > 1) {
        const axis =
          vx > vy
            ? Math.random() < vx / (vx + vy)
              ? 'h'
              : 'v'
            : Math.random() < vy / (vx + vy)
              ? 'v'
              : 'h';
        direction =
          axis === 'h'
            ? velocity.x > 0
              ? 'l'
              : 'r'
            : velocity.y > 0
              ? 't'
              : 'b';
      }

      star.z = STAR_MIN_SCALE + Math.random() * (1 - STAR_MIN_SCALE);

      if (direction === 'z') {
        star.z = 0.1;
        star.x = Math.random() * width;
        star.y = Math.random() * height;
      } else if (direction === 'l') {
        star.x = -OVERFLOW_THRESHOLD;
        star.y = height * Math.random();
      } else if (direction === 'r') {
        star.x = width + OVERFLOW_THRESHOLD;
        star.y = height * Math.random();
      } else if (direction === 't') {
        star.x = width * Math.random();
        star.y = -OVERFLOW_THRESHOLD;
      } else if (direction === 'b') {
        star.x = width * Math.random();
        star.y = height + OVERFLOW_THRESHOLD;
      }
    };

    const resize = () => {
      scale = window.devicePixelRatio || 1;
      width = window.innerWidth * scale;
      height = window.innerHeight * scale;
      canvas.width = width;
      canvas.height = height;
      gradient = context.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, '#0E1110');
      gradient.addColorStop(1, '#0F423A');
      stars.forEach(placeStar);
    };

    const update = () => {
      velocity.tx *= 0.96;
      velocity.ty *= 0.96;
      velocity.x += (velocity.tx - velocity.x) * 0.8;
      velocity.y += (velocity.ty - velocity.y) * 0.8;

      stars.forEach((star) => {
        star.x += velocity.x * star.z;
        star.y += velocity.y * star.z;
        star.x += (star.x - width / 2) * velocity.z * star.z;
        star.y += (star.y - height / 2) * velocity.z * star.z;
        star.z += velocity.z;

        if (
          star.x < -OVERFLOW_THRESHOLD ||
          star.x > width + OVERFLOW_THRESHOLD ||
          star.y < -OVERFLOW_THRESHOLD ||
          star.y > height + OVERFLOW_THRESHOLD
        ) {
          recycleStar(star);
        }
      });
    };

    const render = () => {
      stars.forEach((star) => {
        context.beginPath();
        context.lineCap = 'round';
        context.lineWidth = STAR_SIZE * star.z * scale;
        context.globalAlpha = 0.5 + 0.5 * Math.random();
        context.strokeStyle = STAR_COLOR;

        let tailX = velocity.x * 2;
        let tailY = velocity.y * 2;

        if (Math.abs(tailX) < 0.1) tailX = 0.5;
        if (Math.abs(tailY) < 0.1) tailY = 0.5;

        context.moveTo(star.x, star.y);
        context.lineTo(star.x + tailX, star.y + tailY);
        context.stroke();
      });
    };

    const step = (time: number) => {
      if (!isVisible) {
        animationFrameId = requestAnimationFrame(step);
        return;
      }

      if (time - lastFrameTime >= FRAME_INTERVAL) {
        lastFrameTime = time;
        if (gradient) {
          context.fillStyle = gradient;
          context.fillRect(0, 0, width, height);
        }
        update();
        render();
      }

      animationFrameId = requestAnimationFrame(step);
    };

    const movePointer = (x: number, y: number) => {
      if (typeof pointerX === 'number' && typeof pointerY === 'number') {
        const ox = x - pointerX;
        const oy = y - pointerY;
        velocity.tx += (ox / 8) * scale * (touchInput ? 1 : -1);
        velocity.ty += (oy / 8) * scale * (touchInput ? 1 : -1);
      }
      pointerX = x;
      pointerY = y;
    };

    const onMouseMove = (event: MouseEvent) => {
      touchInput = false;
      movePointer(event.clientX, event.clientY);
    };

    const onTouchMove = (event: TouchEvent) => {
      touchInput = true;
      movePointer(event.touches[0].clientX, event.touches[0].clientY);
      event.preventDefault();
    };

    const onMouseLeave = (): void => {
      pointerX = null;
      pointerY = null;
    };

    const handleVisibilityChange = () => {
      isVisible = document.visibilityState === 'visible';
    };

    generate();
    resize();

    // Add extra delay before starting animation on mobile to reduce CPU impact
    const startAnimationLoop = () => {
      animationFrameId = requestAnimationFrame(step);
    };

    const animationDelay = isMobile ? 2000 : 0; // Increased from 1s to 2s on mobile
    const startTimeout = setTimeout(startAnimationLoop, animationDelay);

    window.addEventListener('resize', resize);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchend', onMouseLeave);
    document.addEventListener('mouseleave', onMouseLeave);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(startTimeout);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onMouseLeave);
      document.removeEventListener('mouseleave', onMouseLeave);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      cancelAnimationFrame(animationFrameId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIdle]);

  useEffect(() => {
    if (isIdle) {
      return initializeStarfield();
    }
  }, [isIdle, initializeStarfield]);

  return (
    <canvas
      ref={canvasRef}
      className="starfield-canvas"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'block',
        background: 'transparent',
        zIndex: -1,
        pointerEvents: 'none',
      }}
    />
  );
};

export default StarfieldBackgroundCanvas;
